import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';

import { Inspector } from '@/components/Inspector';
import { Empty, ErrorCard, LOGIN_NEEDED, PillTag, Seg, Select, Toggle } from '@/components/ui';
import { getJson, useApi, useMeta } from '@/lib/api';
import { cx } from '@/lib/cx';
import { dayHeading, fmtDay, fmtInt, fmtMoney, fmtSeconds, fmtTime } from '@/lib/format';
import { useCustomers, useParam } from '@/lib/params';
import type { FeedGroup, FeedItem, FeedPage } from '@/lib/types';

const GROUP_ORDER: FeedGroup[] = ['asks', 'mcp', 'actions', 'system'];
const RAIL_GROUPS: { key: FeedGroup; label: string }[] = [
  { key: 'asks', label: 'User asks' },
  { key: 'actions', label: 'Product actions' },
  { key: 'mcp', label: 'External AI (MCP)' },
  { key: 'system', label: 'System & runs' },
];
const DEFAULT_SHOW = 'asks,mcp,actions';
const RANGE_OPTIONS = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];
const OUTCOMES = [
  { value: 'any', label: 'Outcome: any' },
  { value: 'not_ok', label: 'Outcome: not ok' },
  { value: 'ok', label: 'Outcome: ok' },
];
const VIEWS: { label: string; params: Record<string, string> }[] = [
  { label: "Today's failures", params: { range: '24h', outcome: 'not_ok', show: 'asks,mcp,system' } },
  { label: 'Failures this week', params: { outcome: 'not_ok', show: 'asks,mcp,system' } },
  { label: 'External AI (MCP)', params: { show: 'mcp' } },
  { label: 'System failures', params: { show: 'system', outcome: 'not_ok' } },
];
const FILTER_KEYS = ['range', 'from', 'to', 'customers', 'show', 'company_id', 'user_id', 'area', 'outcome', 'q'];

function icon(item: FeedItem): { bg: string; fg: string; glyph: string } {
  const failed = item.status === 'error' || item.status === 'abandoned';
  if (item.type === 'ask')
    return failed
      ? { bg: 'var(--bad-bg)', fg: 'var(--bad)', glyph: '●' }
      : { bg: 'var(--good-bg)', fg: 'var(--good)', glyph: '●' };
  if (failed) return { bg: 'var(--bad-bg)', fg: 'var(--bad)', glyph: '▲' };
  return { bg: 'var(--sunk)', fg: 'var(--ink-3)', glyph: '◆' };
}

function EventRow({
  item,
  more,
  selected,
  onSelect,
  onCompany,
  onUser,
}: {
  item: FeedItem;
  more: number;
  selected: boolean;
  onSelect: () => void;
  onCompany: (id: number) => void;
  onUser: (id: number) => void;
}) {
  const small = item.type !== 'ask';
  const ic = icon(item);
  return (
    <div className={cx('ev', small && 'small', selected && 'on')} data-key={item.key} onClick={onSelect}>
      <span className="ic" style={{ background: ic.bg, color: ic.fg }}>
        {ic.glyph}
      </span>
      <div style={{ minWidth: 0 }}>
        <div className="who">
          {item.company ? (
            <b
              onClick={(e) => {
                e.stopPropagation();
                onCompany(item.company!.id);
              }}
            >
              {item.company.name}
            </b>
          ) : (
            <b>No company</b>
          )}
          {item.actor && (
            <>
              {' · '}
              <span
                onClick={
                  item.user_id
                    ? (e) => {
                        e.stopPropagation();
                        onUser(item.user_id!);
                      }
                    : undefined
                }
              >
                {item.actor}
              </span>
            </>
          )}
          {item.area_label && ` · ${item.area_label}`}
        </div>
        <div className="q">
          {item.type === 'ask' ? (
            item.text ? (
              `“${item.text}”`
            ) : (
              <span className="faint">(text cleared after 90 days)</span>
            )
          ) : (
            <>
              {item.text} {small && item.pills.map((p) => <PillTag key={p.label} pill={p} />)}
            </>
          )}
        </div>
        {!small && (item.pills.length > 0 || item.detail || more > 0) && (
          <div className="tg">
            {item.pills.map((p) => (
              <PillTag key={p.label} pill={p} />
            ))}
            {item.detail && (
              <span className="faint" style={{ fontSize: 12 }}>
                {item.detail}
              </span>
            )}
            {more > 0 && <span className="tag">+{more} more in this conversation</span>}
          </div>
        )}
      </div>
      <div className="rt">
        {fmtTime(item.at)}
        {!small && (
          <>
            <br />
            {fmtSeconds(item.duration_s)} · {fmtMoney(item.cost)}
          </>
        )}
      </div>
    </div>
  );
}

function useFeedFilters(): Record<string, string> {
  const [search] = useSearchParams();
  return useMemo(() => {
    const out: Record<string, string> = {};
    FILTER_KEYS.forEach((key) => {
      const value = search.get(key);
      if (value) out[key] = value;
    });
    return out;
  }, [search]);
}

export function FeedPage() {
  const meta = useMeta();
  const navigate = useNavigate();
  const [, setSearch] = useSearchParams();
  const filters = useFeedFilters();
  const [selected, setSelected] = useParam('sel', '');
  const [group, setGroup] = useParam('group', 'ask');
  const [show, setShow] = useParam('show', DEFAULT_SHOW);
  const [outcome, setOutcome] = useParam('outcome', 'any');
  const [range, setRange] = useParam('range', '7d');
  const [area, setArea] = useParam('area', '');
  const [q, setQ] = useParam('q', '');
  const [companyId, setCompanyId] = useParam('company_id', '');
  const [userId, setUserId] = useParam('user_id', '');
  const [customers, setCustomers] = useCustomers();
  const [text, setText] = useState(q);
  const searchRef = useRef<HTMLInputElement>(null);

  const feed = useInfiniteQuery({
    queryKey: ['/feed', filters],
    queryFn: ({ pageParam }) => getJson<FeedPage>('/feed', { ...filters, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  const first = feed.data?.pages[0];
  const items = useMemo(() => feed.data?.pages.flatMap((p) => p.items) ?? [], [feed.data]);
  const since = first?.window.end;
  const fresh = useApi<{ count: number }>(
    '/feed/new',
    { ...filters, since },
    { enabled: Boolean(since), refetchInterval: 60_000 },
  );

  const visible = useMemo(() => {
    if (group !== 'conversation') return items.map((item) => ({ item, more: 0 }));
    const firstOfThread = new Map<string, { item: FeedItem; more: number }>();
    const rows: { item: FeedItem; more: number }[] = [];
    items.forEach((item) => {
      const thread = item.type === 'ask' && item.thread_key ? `${item.company?.id}:${item.thread_key}` : null;
      const existing = thread ? firstOfThread.get(thread) : undefined;
      if (existing) {
        existing.more += 1;
        return;
      }
      const row = { item, more: 0 };
      if (thread) firstOfThread.set(thread, row);
      rows.push(row);
    });
    return rows;
  }, [items, group]);

  useEffect(() => setText((t) => (t.trim() === q ? t : q)), [q]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (text.trim() !== q) setQ(text.trim() || null);
    }, 400);
    return () => clearTimeout(timer);
  }, [text, q, setQ]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      const keys = visible.map((v) => v.item.key);
      const at = keys.indexOf(selected);
      const next = e.key === 'ArrowDown' ? Math.min(keys.length - 1, at + 1) : Math.max(0, at - 1);
      if (keys[next]) setSelected(keys[next]);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, selected, setSelected]);

  useEffect(() => {
    if (selected) document.querySelector(`[data-key="${CSS.escape(selected)}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  const shown = new Set(show.split(',').filter(Boolean));
  const toggleGroup = (g: FeedGroup) => {
    const next = new Set(shown);
    if (next.has(g)) next.delete(g);
    else next.add(g);
    setShow(GROUP_ORDER.filter((k) => next.has(k)).join(',') || 'asks');
  };
  const companyName = items.find((i) => String(i.company?.id) === companyId)?.company?.name ?? `#${companyId}`;
  const userName = items.find((i) => String(i.user_id) === userId)?.actor ?? `#${userId}`;
  const areaLabel = meta.data?.areas.find((a) => a.key === area)?.label ?? area;
  const viewActive = (params: Record<string, string>) =>
    Object.keys(filters).length === Object.keys(params).length &&
    Object.entries(params).every(([k, v]) => filters[k] === v);

  const chips: { label: string; clear: () => void }[] = [];
  if (outcome !== 'any')
    chips.push({ label: `Outcome: ${outcome === 'not_ok' ? 'not ok' : 'ok'}`, clear: () => setOutcome(null) });
  if (area) chips.push({ label: `Area: ${areaLabel}`, clear: () => setArea(null) });
  if (companyId) chips.push({ label: `Company: ${companyName}`, clear: () => setCompanyId(null) });
  if (userId) chips.push({ label: `User: ${userName}`, clear: () => setUserId(null) });
  if (q) chips.push({ label: `Search: “${q}”`, clear: () => setQ(null) });
  if (filters.from && filters.to) {
    chips.push({
      label: `${fmtDay(filters.from)} – ${fmtDay(filters.to)}`,
      clear: () =>
        setSearch(
          (current) => {
            const p = new URLSearchParams(current);
            p.delete('from');
            p.delete('to');
            return p;
          },
          { replace: true },
        ),
    });
  } else if (range !== '7d') {
    chips.push({ label: RANGE_OPTIONS.find((r) => r.value === range)?.label ?? range, clear: () => setRange(null) });
  }

  let lastHeading = '';
  const now = meta.data?.now ?? new Date().toISOString();
  const volume = first?.volume;
  const volumeMax = Math.max(1, ...(volume?.buckets.map((b) => b.ok + b.not_ok) ?? []));

  return (
    <div className="feed">
      <aside className="rail">
        <div className="sec">Show</div>
        {RAIL_GROUPS.map((g) => (
          <div key={g.key} className={cx('chk', shown.has(g.key) && 'on')} onClick={() => toggleGroup(g.key)}>
            <span className="box">{shown.has(g.key) && '✓'}</span>
            {g.label}
            <span className="c">{first?.counts ? fmtInt(first.counts[g.key]) : ''}</span>
          </div>
        ))}
        <div className="sec">Filter</div>
        <div
          className={cx('sel', companyId && 'set')}
          title={companyId ? 'Clear' : 'Click a company name in the feed to filter by it'}
          onClick={() => companyId && setCompanyId(null)}
        >
          {companyId ? `${companyName} ✕` : 'Any company'}
        </div>
        <div
          className={cx('sel', userId && 'set')}
          title={userId ? 'Clear' : 'Click a person in the feed to filter by them'}
          onClick={() => userId && setUserId(null)}
        >
          {userId ? `${userName} ✕` : 'Any user'}
        </div>
        <Select
          value={area}
          active={Boolean(area)}
          onChange={(v) => setArea(v || null)}
          options={[
            { value: '', label: 'Any area' },
            ...(meta.data?.areas ?? []).map((a) => ({ value: a.key, label: a.label })),
          ]}
        />
        <Select value={outcome} active={outcome !== 'any'} onChange={setOutcome} options={OUTCOMES} />
        <Select value={range} onChange={setRange} options={RANGE_OPTIONS} />
        <div style={{ marginTop: 6 }}>
          <Toggle on={customers} label="Customers only" onChange={setCustomers} />
        </div>
        <div className="sec">Saved views</div>
        {VIEWS.map((v) => (
          <div
            key={v.label}
            className={cx('view', viewActive(v.params) && 'on')}
            onClick={() => navigate(`/feed?${new URLSearchParams(v.params)}`)}
          >
            {v.label}
          </div>
        ))}
        <div className="link disabled" title={LOGIN_NEEDED} style={{ marginTop: 6, fontSize: 12 }}>
          + Save current view
        </div>
      </aside>

      <section className="stream">
        <div className="sbar">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div className="search" style={{ flex: 1 }}>
              <span className="faint">⌕</span>
              <input
                ref={searchRef}
                value={text}
                placeholder="Search what people asked and what the AI answered…"
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && setQ(text.trim() || null)}
              />
            </div>
            <Seg options={[{ value: 'words', label: 'Words' }]} value="words" onChange={() => undefined} compact />
          </div>
          <div className="chips">
            {chips.map((c) => (
              <span key={c.label} className="fchip">
                {c.label}
                <span className="x" onClick={c.clear}>
                  ✕
                </span>
              </span>
            ))}
            {chips.length > 0 && (
              <span className="link" style={{ fontSize: 12 }} onClick={() => navigate('/feed')}>
                Clear all
              </span>
            )}
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }} className="muted">
              Group by
              <Seg
                options={[
                  { value: 'ask', label: 'Ask' },
                  { value: 'conversation', label: 'Conversation' },
                ]}
                value={group === 'conversation' ? 'conversation' : 'ask'}
                onChange={setGroup}
                compact
              />
            </span>
          </div>
          {volume && first && (
            <>
              <div className="histo" title="Events per bucket · grey = ok, red = not ok">
                {volume.buckets.map((b) => (
                  <div
                    key={b.start}
                    style={{ height: '100%' }}
                    title={`${b.ok + b.not_ok} events · ${b.not_ok} not ok`}
                  >
                    <span className="ok" style={{ height: `${(b.ok / volumeMax) * 100}%` }} />
                    <span className="nk" style={{ height: `${(b.not_ok / volumeMax) * 100}%` }} />
                  </div>
                ))}
              </div>
              <div
                className="faint"
                style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginTop: 4 }}
              >
                <span>{fmtDay(first.window.start)}</span>
                <span>
                  <span style={{ color: 'var(--line-strong)' }}>■</span> ok{' '}
                  <span style={{ color: 'var(--bad)' }}>■</span> not ok · {fmtInt(volume.not_ok_total)} of{' '}
                  {fmtInt(volume.total)} events
                </span>
                <span>{fmtDay(first.window.end)}</span>
              </div>
            </>
          )}
        </div>
        {fresh.data && fresh.data.count > 0 && (
          <div className="newpill">
            <span onClick={() => feed.refetch()}>↑ {fresh.data.count} new since you opened this</span>
          </div>
        )}
        {feed.error && !feed.data ? (
          <div style={{ padding: 16 }}>
            <ErrorCard error={feed.error} />
          </div>
        ) : !feed.data ? (
          [0, 1, 2].map((i) => (
            <div key={i} className="ev">
              <div className="skel" style={{ width: 20, height: 20 }} />
              <div>
                <div className="skel" style={{ height: 10, width: '40%', marginBottom: 6 }} />
                <div className="skel" style={{ height: 12, width: '85%', marginBottom: 6 }} />
                <div className="skel" style={{ height: 10, width: '50%' }} />
              </div>
            </div>
          ))
        ) : visible.length === 0 ? (
          <Empty
            icon="⌕"
            title={q ? `Nothing matches “${q}” with these filters` : 'Nothing here with these filters'}
            actions={
              outcome !== 'any' && (
                <button type="button" className="btn" onClick={() => setOutcome(null)}>
                  Remove outcome filter
                </button>
              )
            }
          />
        ) : (
          <>
            {visible.map(({ item, more }) => {
              const heading = dayHeading(item.at, now);
              const separator = heading !== lastHeading;
              lastHeading = heading;
              return (
                <Fragment key={item.key}>
                  {separator && <div className="day">{heading}</div>}
                  <EventRow
                    item={item}
                    more={more}
                    selected={item.key === selected}
                    onSelect={() => setSelected(item.key)}
                    onCompany={(id) => setCompanyId(String(id))}
                    onUser={(id) => setUserId(String(id))}
                  />
                </Fragment>
              );
            })}
            {(feed.hasNextPage || feed.error) && (
              <div style={{ padding: 12, textAlign: 'center' }}>
                {feed.hasNextPage && (
                  <button
                    type="button"
                    className="btn"
                    disabled={feed.isFetchingNextPage}
                    onClick={() => feed.fetchNextPage()}
                  >
                    {feed.isFetchingNextPage ? 'Loading…' : 'Load more'}
                  </button>
                )}
                {feed.error && (
                  <span className="note" style={{ marginLeft: 8 }}>
                    {feed.error.message}
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </section>

      <aside className="insp">
        <Inspector selected={selected} item={items.find((i) => i.key === selected) ?? null} onSelect={setSelected} />
      </aside>
    </div>
  );
}
